import chartPalette from '../theme/chartPalette.json';

const STORAGE_KEY = 'garp.symbolColors.v1';

type PaletteNode = { $type?: string; $value?: string };

type ChartPalette = {
  Chart: Record<string, PaletteNode>;
};

type StoredMap = Record<string, string>; // SYMBOL -> paletteKey (e.g. "07")

const palette = chartPalette as unknown as ChartPalette;

const normalizeSymbol = (raw: string): string => String(raw || '').trim().toUpperCase();

const isNumericKey = (k: string) => /^\d+$/.test(k);

const stockPaletteKeys = (() => {
  const keys = Object.keys(palette.Chart || {}).filter(isNumericKey);
  keys.sort((a, b) => Number(a) - Number(b));
  return keys;
})();

const getPaletteValue = (key: string): string | null => {
  const node = palette.Chart?.[key];
  const value = node?.$value;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const getSpecialColor = (key: 'CurrentPrice' | 'BuyPrice'): string => {
  return getPaletteValue(key) || (key === 'CurrentPrice' ? '#2563eb' : '#6b7280');
};

export const CHART_COLORS = {
  currentPrice: getSpecialColor('CurrentPrice'),
  buyPrice: getSpecialColor('BuyPrice'),
};

let cached: StoredMap | null = null;

const loadMap = (): StoredMap => {
  if (cached) return cached;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      cached = {};
      return cached;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      cached = {};
      return cached;
    }

    const obj = parsed as Record<string, unknown>;
    const next: StoredMap = {};
    for (const [symRaw, keyRaw] of Object.entries(obj)) {
      const sym = normalizeSymbol(symRaw);
      const key = typeof keyRaw === 'string' ? keyRaw : '';
      if (!sym) continue;
      if (!key) continue;
      if (!stockPaletteKeys.includes(key)) continue;
      next[sym] = key;
    }

    cached = next;
    return cached;
  } catch {
    cached = {};
    return cached;
  }
};

const saveMap = (map: StoredMap) => {
  cached = map;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
};

const randomInt = (maxExclusive: number): number => {
  const max = Math.max(0, Math.floor(maxExclusive));
  if (max <= 1) return 0;

  try {
    const cryptoObj = window.crypto;
    if (cryptoObj?.getRandomValues) {
      const arr = new Uint32Array(1);
      cryptoObj.getRandomValues(arr);
      return arr[0] % max;
    }
  } catch {
    // ignore
  }

  return Math.floor(Math.random() * max);
};

const hashSymbol = (symbol: string) => {
  const s = normalizeSymbol(symbol);
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
};

const keyFromHash = (symbol: string): string => {
  if (stockPaletteKeys.length === 0) return '01';
  const idx = hashSymbol(symbol) % stockPaletteKeys.length;
  return stockPaletteKeys[idx];
};

export const getSymbolColor = (symbolRaw: string): string => {
  const symbol = normalizeSymbol(symbolRaw);
  if (!symbol) return '#94a3b8';
  if (symbol === 'OTHER') return '#94a3b8';

  const map = loadMap();
  const key = map[symbol] || keyFromHash(symbol);
  return getPaletteValue(key) || '#60a5fa';
};

export const ensureSymbolColorAssigned = (symbolRaw: string): { color: string; changed: boolean } => {
  const symbol = normalizeSymbol(symbolRaw);
  if (!symbol || symbol === 'OTHER') return { color: getSymbolColor(symbol), changed: false };

  const map = loadMap();
  const existingKey = map[symbol];
  if (existingKey && stockPaletteKeys.includes(existingKey)) {
    return { color: getPaletteValue(existingKey) || getSymbolColor(symbol), changed: false };
  }

  const usedKeys = new Set(Object.values(map));
  const available = stockPaletteKeys.filter((k) => !usedKeys.has(k));
  const pickFrom = available.length > 0 ? available : stockPaletteKeys;
  const nextKey = pickFrom[randomInt(pickFrom.length)];

  const next: StoredMap = { ...map, [symbol]: nextKey };
  saveMap(next);

  return { color: getPaletteValue(nextKey) || getSymbolColor(symbol), changed: true };
};

export const ensureSymbolColorsAssigned = (symbols: string[]): boolean => {
  let changed = false;
  for (let i = 0; i < symbols.length; i++) {
    const res = ensureSymbolColorAssigned(symbols[i]);
    if (res.changed) changed = true;
  }
  return changed;
};

const hexToRgba = (hex: string, alpha: number): string => {
  const a = Math.max(0, Math.min(1, Number(alpha)));
  const raw = String(hex || '').trim();
  const h = raw.replace('#', '');
  if (!(h.length === 6 || h.length === 3)) return `rgba(148,163,184,${a})`;

  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);

  if (![r, g, b].every(Number.isFinite)) return `rgba(148,163,184,${a})`;
  return `rgba(${r},${g},${b},${a})`;
};

export const getSymbolColorRgba = (symbol: string, alpha: number): string => {
  return hexToRgba(getSymbolColor(symbol), alpha);
};
